// Forwards client-side errors to the server's admin error log via
// /api/self-capture. Fire-and-forget — never throws, never blocks the caller.
//
// Used by:
// - main.tsx — global window.error / unhandledrejection / ErrorBoundary
// - auth.tsx — authFetch when an API responds non-OK (these never reach the
//   global handlers because authFetch always throws into a caller try/catch)

import { DASHBOARD_PATHS, PATHS } from './paths';

export interface ClientError {
  message: string;
  stack?: string;
  componentStack?: string;
  url?: string;
}

export function reportError(data: ClientError): void {
  try {
    navigator.sendBeacon(
      PATHS.selfCapture,
      new Blob(
        [JSON.stringify({ ...data, userAgent: navigator.userAgent })],
        { type: 'application/json' },
      ),
    );
  } catch {}
}

// Cloudflare-specific 5xx (520–530) plus the standard gateway codes mean the
// edge couldn't reach / get a clean answer from our origin — they never came
// from an Express route.
const GATEWAY_STATUSES = new Set([502, 503, 504, 520, 521, 522, 523, 524, 525, 526, 527, 530]);

// Distinguish an edge/origin blip from a genuine app fault. Our `errorHandler`
// ALWAYS sends `{ error: "Internal server error" }`, so a 5xx that arrives with
// no body and no statusText could not have been produced by our app — it's
// Cloudflare/Caddy answering for an origin that was briefly unreachable or
// event-loop-blocked. Tagging these keeps the admin error log honest: an infra
// blip reads as infra, not as a phantom bug in whatever route happened to be
// in flight.
function isGatewayError(status: number, message: string): boolean {
  if (GATEWAY_STATUSES.has(status)) return true;
  return status >= 500 && status < 600 && message.trim() === '';
}

// A single origin blip knocks out every concurrent request at once — the three
// sidebar badge polls (chat unread, chat mentions, notifications) all fail in
// the same millisecond and each fires a report. Coalesce that burst so the log
// shows one infra event instead of a fan-out of duplicates. App (non-gateway)
// errors are never coalesced — each is a distinct signal.
let lastGatewayReportAt = 0;
const GATEWAY_COALESCE_MS = 10_000;

// Pulls the edge/app correlation id off a response so a reported 5xx can be
// cross-referenced with Cloudflare / server logs instead of being a dead end.
export function correlationIdFromHeaders(headers?: Headers): string | undefined {
  if (!headers) return undefined;
  return headers.get('cf-ray') ?? headers.get('x-request-id') ?? undefined;
}

// Wrapper for non-OK API responses. Skips auth-state failures (401) and the
// refresh endpoint, which are expected during normal session lifecycle.
export function reportApiError(args: {
  url: string;
  status: number;
  message: string;
  method?: string;
  /** cf-ray / x-request-id, for correlating with edge + server logs. */
  requestId?: string;
}): void {
  if (args.status === 401) return;
  if (args.url.includes(DASHBOARD_PATHS.authRefresh)) return;

  const gateway = isGatewayError(args.status, args.message);
  if (gateway) {
    const now = Date.now();
    if (now - lastGatewayReportAt < GATEWAY_COALESCE_MS) return;
    lastGatewayReportAt = now;
  }

  const kind = gateway ? 'gateway' : 'api';
  const detail = args.message.trim() || (gateway ? 'origin unreachable' : 'no body');
  const ray = args.requestId ? ` ray=${args.requestId}` : '';
  reportError({
    message: `[${kind}] ${args.status} ${args.method ?? 'GET'} ${args.url}: ${detail}${ray}`,
    url: location.href,
  });
}
